import type { MixedItemTag } from "@memorang/types";
import type { ItemTypeSession } from "@memorang/ui/components/ItemTypeDetailModal/types";
import type { RecommendedAction } from "@memorang/ui/components/RecommendedActionsDialog/types";
import type { IconType, LevelIconName } from "@memorang/ui/icon";
import type { TFunction } from "i18next";
import { buildRecommendedStudyTasks } from "../dashboard-integration";
import type { RecommendationResult, RecommendedStudySession } from "../types";
import type { RecommendedSkillTask } from "./dashboard-task-types";
import { capLaunchDistribution } from "./launch-distribution";

export type FrontendQuickStudySession = RecommendedAction & {
	tagIds: number[];
	distribution: RecommendedStudySession["quickStudyMeta"]["distribution"];
	numItems: number;
};

export type FrontendRecommendationOption = {
	id: string;
	task: RecommendedSkillTask;
	quickStudySession: FrontendQuickStudySession;
	itemTypeSession: ItemTypeSession;
	insight: string;
};

const PRESENTATION: Record<5 | 10 | 20, { titleKey: string; icon: IconType }> =
	{
		5: {
			titleKey: "sessionConfig.quickStudy.levelSessions.rapidRecall",
			icon: "rocket-launch-outline",
		},
		10: {
			titleKey: "sessionConfig.quickStudy.levelSessions.purposefulPractice",
			icon: "golf",
		},
		20: {
			titleKey: "sessionConfig.quickStudy.levelSessions.focusFlow",
			icon: "tsunami",
		},
	};

type OptionTags = {
	skill: MixedItemTag;
	proficiency: MixedItemTag;
	questionType: MixedItemTag;
};

const resolveOptionTags = (
	session: RecommendedStudySession,
	skills: MixedItemTag[],
	proficiencies: MixedItemTag[],
	questionTypes: MixedItemTag[],
): OptionTags | null => {
	const skill = skills.find(({ id }) => id === session.tagIds.skill);
	const proficiency = proficiencies.find(
		({ id }) => id === session.tagIds.proficiency,
	);
	const questionType = questionTypes.find(
		({ id }) => id === session.tagIds.questionType,
	);
	return skill && proficiency && questionType
		? { skill, proficiency, questionType }
		: null;
};

const presentationFor = (session: RecommendedStudySession) =>
	session.requestedDurationMinutes
		? PRESENTATION[session.requestedDurationMinutes]
		: undefined;

const buildOption = ({
	session,
	examId,
	skills,
	proficiencies,
	questionTypes,
	translate,
	proficiencyIconMap,
	skillIconMap,
	questionTypeIconMap,
}: {
	session: RecommendedStudySession;
	examId: string;
	skills: MixedItemTag[];
	proficiencies: MixedItemTag[];
	questionTypes: MixedItemTag[];
	translate: TFunction;
	proficiencyIconMap: Record<string, LevelIconName>;
	skillIconMap: Record<string, IconType>;
	questionTypeIconMap: Record<string, IconType>;
}): FrontendRecommendationOption | null => {
	const task = buildRecommendedStudyTasks({
		result: { status: "READY", examId, sessions: [session] },
		skills,
		proficiencies,
		questionTypes,
		translate,
	})[0];
	const tags = resolveOptionTags(session, skills, proficiencies, questionTypes);
	if (!task || !tags) {
		return null;
	}

	const { skill, proficiency, questionType } = tags;
	const id = `${session.cellId}:${session.requestedDurationMinutes ?? session.quickStudyMeta.minutes}`;
	const minutes = session.quickStudyMeta.minutes;
	const durationLabel = translate("sessionConfig.quickStudy.duration", {
		minutes,
	});
	const presentation = presentationFor(session);
	const questionTypeIcon =
		questionTypeIconMap[questionType.title] ?? "text-box-outline";
	return {
		id,
		task,
		quickStudySession: {
			id,
			icon: skillIconMap[skill.title] ?? questionTypeIcon,
			title: `${skill.title} · ${questionType.title}`,
			subtitle: task.body ?? "",
			badge: { label: durationLabel },
			description: translate("sessionConfig.quickStudy.summary", {
				count: session.actualItemCount,
				skill: skill.title,
				minutes,
				context: "questionType",
				questionType: questionType.title,
				defaultValue: `Study ${session.actualItemCount} items from ${skill.title}, ${questionType.title}. Estimated ${minutes} minutes.`,
			}),
			tagIds: [
				session.tagIds.skill,
				session.tagIds.proficiency,
				session.tagIds.questionType,
			],
			distribution: capLaunchDistribution(
				session.quickStudyMeta.distribution,
				session.quickStudyMeta.numItems,
			),
			numItems: session.quickStudyMeta.numItems,
		},
		itemTypeSession: {
			id,
			icon: presentation?.icon ?? questionTypeIcon,
			title: translate(
				presentation?.titleKey ??
					"sessionConfig.quickStudy.levelSessions.purposefulPractice",
			),
			itemsCount: session.actualItemCount,
			itemsLabel: translate(
				"sessionConfig.quickStudy.levelSessions.itemsLabel",
				{ level: proficiency.title },
			),
			tags: [
				{
					icon: proficiencyIconMap[proficiency.title] ?? "school-outline",
				},
				{ icon: "clock-outline", label: durationLabel },
			],
		},
		insight: task.body ?? "",
	};
};

export const buildFrontendRecommendationOptions = ({
	result,
	skills,
	proficiencies,
	questionTypes,
	translate,
	proficiencyIconMap = {},
	skillIconMap = {},
	questionTypeIconMap = {},
}: {
	result: RecommendationResult;
	skills: MixedItemTag[];
	proficiencies: MixedItemTag[];
	questionTypes: MixedItemTag[];
	translate: TFunction;
	proficiencyIconMap?: Record<string, LevelIconName>;
	skillIconMap?: Record<string, IconType>;
	questionTypeIconMap?: Record<string, IconType>;
}): FrontendRecommendationOption[] => {
	if (result.status !== "READY") {
		return [];
	}
	return result.sessions.flatMap((session) => {
		const option = buildOption({
			session,
			examId: result.examId,
			skills,
			proficiencies,
			questionTypes,
			translate,
			proficiencyIconMap,
			skillIconMap,
			questionTypeIconMap,
		});
		return option ? [option] : [];
	});
};
